"use client";

import { usePdpVariantNavigation } from "./variant-navigation-context";

import { cn } from "@/lib/utils";
import styles from "./pdp-layout.module.css";

export interface DoseOption {
	variantId: string;
	label: string; // e.g. "10mg" / "5ml"
	priceLabel: string; // formatted price string
	available: boolean;
}

interface DosePickerProps {
	options: DoseOption[];
	selectedVariantId: string | undefined;
	ariaLabel: string;
	className?: string;
}

export function DosePicker({ options, selectedVariantId, ariaLabel, className }: DosePickerProps) {
	const navigation = usePdpVariantNavigation();
	if (!navigation) throw new Error("DosePicker requires PdpVariantNavigationProvider");
	const { pending: isPending, selectVariant: select } = navigation;

	return (
		<div
			role="radiogroup"
			aria-label={ariaLabel}
			data-option-count={options.length}
			className={cn(styles.doseGrid, className)}
		>
			{options.map((opt) => {
				const active = opt.variantId === selectedVariantId;
				return (
					<button
						key={opt.variantId}
						type="button"
						role="radio"
						aria-checked={active}
						disabled={isPending}
						onClick={() => select(opt.variantId)}
						className={cn(
							"group/dose relative flex min-h-14 min-w-0 flex-col items-center justify-center gap-0.5 rounded-full border-2 px-2 py-2.5 transition-colors",
							active
								? "border-primary-hero bg-primary-hero text-primary-hero-foreground shadow-md"
								: "hover:bg-primary-hero/[0.05] border-border bg-background text-foreground hover:border-primary-hero",
							!opt.available && "opacity-50",
						)}
					>
						<span className="font-mono text-[0.78rem] font-semibold tabular-nums tracking-tight">
							{opt.label}
						</span>
						<span
							className={cn(
								"break-words text-center font-mono text-[0.6875rem] tabular-nums leading-snug tracking-tight",
								active ? "text-primary-hero-foreground/80" : "text-muted-foreground",
							)}
						>
							{opt.priceLabel}
						</span>
					</button>
				);
			})}
		</div>
	);
}
